/**
 * CAOS-564 — the METADATA search overlay: one place, reachable from every page, that answers "what
 * is this actually called" without leaving the page the question was asked on.
 *
 * The workspace already has a global search (CAOS-135, in the topbar's centre slot) and it answers a
 * different question: it searches RECORDS. This searches the org's METADATA — objects, fields,
 * pages, layouts, roles — through the kernel's `metadata/search` verb.
 *
 * ── WHY AN OVERLAY AND NOT A SCREEN ─────────────────────────────────────────────────────────────
 * Because of WHEN the question gets asked. Somebody looking at a record wonders what a field is
 * really called, or which layout it is on. Today the only answer is to leave the page, find an
 * administration screen, and lose the place they were in — so the question mostly goes unasked. An
 * overlay answers it where it was asked and hands back the thing rather than a search result.
 *
 * ── WHERE A HIT LEADS, AND WHY THAT IS NOT A TABLE IN THIS FILE ─────────────────────────────────
 * A hit carries its metatype. Which SCREEN browses that metatype, and at what address, is a property
 * of what is installed — Setup supplies those screens today and another package could supply them
 * tomorrow — so it is read from the installed `rosterPage` components, each of which names the
 * metatype it `browses` beside the `detailRoute` its rows open. A destination table written here
 * would be this file asserting one package's internal routing, and it would be wrong the first time
 * that package moved a route. This is the same division {@link searchDestinations} already draws for
 * a record hit resolving to an app tab.
 *
 * ── ACCESS ──────────────────────────────────────────────────────────────────────────────────────
 * This surface draws what came back and never decides visibility itself. `metadata/search` carries
 * the same `setup.access` requirement every other metadata read carries, so a person who can search
 * is exactly a person who could already have browsed to the same components. There is no second
 * picture of access here to drift from the kernel's.
 */

import { preference, setPreference } from '../person-preferences.js';
import { fillPath } from '../predicate.js';

/** A minimal response shape, so a test stub need not build a whole `Response`. */
export interface MetadataSearchFetchResponse {
  json(): Promise<unknown>;
}

/** The fetch seam: a same-origin GET against the host `/api/*` proxy. */
export type MetadataSearchFetch = (path: string) => Promise<MetadataSearchFetchResponse>;

const defaultFetch: MetadataSearchFetch = (path) =>
  fetch(path, {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  }) as unknown as Promise<MetadataSearchFetchResponse>;

let searchFetch: MetadataSearchFetch = defaultFetch;

/** Override the fetch seam (tests); `null` restores the default. */
export function setMetadataSearchFetch(fn: MetadataSearchFetch | null): void {
  searchFetch = fn ?? defaultFetch;
}

/** One component the kernel matched — the host projection of `metadata/search`'s hit. */
export interface MetadataSearchHit {
  readonly type: string;
  readonly key: string;
  readonly label: string;
  readonly matchedOn: 'key' | 'label';
}

export interface MetadataSearchGroup {
  readonly type: string;
  readonly hits: readonly MetadataSearchHit[];
  readonly total: number;
}

/** Which edge the overlay is pinned to. */
export type MetadataSearchEdge = 'left' | 'right' | 'bottom';

export const EDGES: readonly MetadataSearchEdge[] = ['left', 'right', 'bottom'];

/**
 * The person's own settings. Both are per-person preferences (CAOS-470's store), so they follow
 * somebody between machines rather than living in one browser.
 */
export const EDGE_PREFERENCE = 'workspace.metadataSearch.edge';
export const OFF_PREFERENCE = 'workspace.metadataSearch.off';

/** Where this person put it, defaulting to the right edge. An unrecognised stored value is ignored. */
export function currentEdge(): MetadataSearchEdge {
  const stored = preference<string>(EDGE_PREFERENCE);
  return EDGES.includes(stored as MetadataSearchEdge) ? (stored as MetadataSearchEdge) : 'right';
}

/**
 * Whether this person has turned it off entirely. Off means the shortcut does nothing and nothing is
 * drawn — a person who does not want it should not have it intercepting a keystroke.
 */
export function isTurnedOff(): boolean {
  return preference<boolean>(OFF_PREFERENCE) === true;
}

/** One installed roster page, as much of it as a destination needs. */
export interface RosterPageRef {
  /** The metatype this roster browses; absent ⇒ it browses something that is not metadata. */
  readonly browses?: string | undefined;
  /** The `{id}`-templated path a row opens; absent ⇒ rows do not open. */
  readonly detailRoute?: string | undefined;
}

/**
 * Metatype → the address one of its components opens at, from what is INSTALLED.
 *
 * The first roster declaring a metatype wins, so two packages browsing the same kind give a hit one
 * address rather than an arbitrary one per search. A roster that browses a kind but opens no row
 * contributes nothing here: a link that leads nowhere is worse than saying plainly that the thing
 * exists, which is the same judgement CAOS-135 made for a record in an object no app shows.
 */
export function metadataDestinations(rosters: readonly RosterPageRef[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const r of rosters) {
    if (!r.browses || !r.detailRoute) continue;
    if (!out.has(r.browses)) out.set(r.browses, r.detailRoute);
  }
  return out;
}

/**
 * Fill a roster's `detailRoute` for one hit, through the SAME `fillPath` the roster renderer itself
 * uses — not a second reading of the template that could fill it differently.
 *
 * A `detailRoute` may name any token, not only `{id}`: a field's identity is two parts, and a roster
 * browsing fields would reasonably address it as `{objectApiName}/{fieldApiName}`. A search hit
 * carries a metatype and a key and nothing else, so a template naming anything this hit cannot
 * answer comes back INCOMPLETE and gets no link at all. That is the same refusal the roster makes
 * for the same reason: half a path lands somewhere arbitrary, and a link with a literal `{token}`
 * left in it is worse than the honest "no screen installed for this kind" this surface already says.
 */
export function destinationFor(destinations: ReadonlyMap<string, string>, hit: MetadataSearchHit): string | null {
  const template = destinations.get(hit.type);
  if (!template) return null;
  const { path, complete } = fillPath(template, { id: hit.key, key: hit.key, type: hit.type, label: hit.label });
  return complete ? path : null;
}

/** A metatype turned into something a person reads: `pageLayout` ⇒ "Page Layout". */
export function kindLabel(type: string): string {
  const spaced = type.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
  return spaced
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export interface MetadataSearchOverlayOptions {
  /** In-app navigation. A hit that resolves to an address routes through this, never `location`. */
  readonly navigate?: ((path: string) => void) | undefined;
  /** The installed roster pages, for resolving where a hit leads. */
  readonly rosters?: readonly RosterPageRef[] | undefined;
}

export interface MetadataSearchOverlay {
  readonly element: HTMLElement;
  open(): void;
  close(): void;
  isOpen(): boolean;
  /** Run one search and paint it. Exposed so a test drives the same path a keystroke drives. */
  search(term: string): Promise<void>;
  /** Move it, and remember where it was put. */
  moveTo(edge: MetadataSearchEdge): Promise<void>;
  destroy(): void;
}

/**
 * Build the overlay. It is created hidden and costs nothing until it is opened: no search runs, and
 * the installed roster pages are read once, on first open, rather than on every page paint.
 */
export function buildMetadataSearchOverlay(options: MetadataSearchOverlayOptions = {}): MetadataSearchOverlay {
  const root = document.createElement('div');
  root.className = 'caos-metadata-search';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-label', 'Search metadata');
  root.hidden = true;
  root.dataset.edge = currentEdge();

  const head = document.createElement('div');
  head.className = 'caos-metadata-search__head';

  const input = document.createElement('input');
  input.type = 'search';
  input.className = 'caos-metadata-search__input';
  input.setAttribute('aria-label', 'Search metadata');
  input.placeholder = 'Search objects, fields, pages, roles…';

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'caos-metadata-search__close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '×';

  head.append(input, closeBtn);

  // The kind filter. It is built from what the SEARCH returned, not from a list of metatypes written
  // here: a narrowing control offering kinds this org has none of is a control that mostly does
  // nothing, and a hardcoded list goes stale the first time a metatype is added.
  const kinds = document.createElement('div');
  kinds.className = 'caos-metadata-search__kinds';

  const results = document.createElement('div');
  results.className = 'caos-metadata-search__results';

  const status = document.createElement('p');
  status.className = 'caos-metadata-search__status';
  // Announced politely: a result count that arrives while somebody is still typing should be
  // readable, not interrupt them.
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');

  root.append(head, kinds, status, results);

  let open = false;
  let destinations = new Map<string, string>(metadataDestinations(options.rosters ?? []).entries());
  let rostersLoaded = (options.rosters ?? []).length > 0;
  /** The one in-flight roster read, so concurrent searches share it rather than racing. */
  let rosterLoad: Promise<void> | null = null;
  let activeKind: string | null = null;
  let lastTerm = '';
  /** Which search is the newest. A slow earlier answer must never replace a newer one. */
  let issued = 0;

  const close = (): void => {
    if (!open) return;
    open = false;
    root.hidden = true;
  };

  /**
   * Read the installed roster pages ONCE, to learn where each kind of component opens. Failure here
   * is not failure of the search: the results still list what matched, they simply do not link, which
   * is the honest degradation rather than an empty overlay.
   */
  const loadRosters = async (): Promise<void> => {
    if (rostersLoaded) return;
    // Marked loaded only on SUCCESS. Setting it before the fetch would turn one bad moment on the
    // network into every kind reading "no screen installed" for the life of the page — a permanent
    // degradation that looks exactly like nothing being installed, with no retry to correct it.
    // The in-flight promise is what keeps a burst of keystrokes from each starting their own read.
    if (!rosterLoad) {
      rosterLoad = (async () => {
        const res = await searchFetch('/api/metadata/retrieve?type=rosterPage');
        const body = (await res.json()) as { components?: { body?: RosterPageRef }[] } | null;
        const rosters = (body?.components ?? []).map((c) => c.body ?? {});
        destinations = metadataDestinations(rosters);
        rostersLoaded = true;
      })();
    }
    try {
      await rosterLoad;
    } catch {
      // Hits are named rather than linked for THIS search, and the next one tries again.
      rosterLoad = null;
    }
  };

  function drawKinds(groups: readonly MetadataSearchGroup[]): void {
    kinds.replaceChildren();
    if (groups.length <= 1) return; // Nothing to narrow between.
    const all = document.createElement('button');
    all.type = 'button';
    all.className = 'caos-metadata-search__kind';
    all.textContent = 'Everything';
    all.setAttribute('aria-pressed', String(activeKind === null));
    all.addEventListener('click', () => {
      activeKind = null;
      void run(lastTerm);
    });
    kinds.append(all);
    for (const g of groups) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'caos-metadata-search__kind';
      b.textContent = `${kindLabel(g.type)} (${g.total})`;
      b.setAttribute('aria-pressed', String(activeKind === g.type));
      b.addEventListener('click', () => {
        activeKind = g.type;
        void run(lastTerm);
      });
      kinds.append(b);
    }
  }

  function drawGroups(groups: readonly MetadataSearchGroup[]): void {
    results.replaceChildren();
    for (const g of groups) {
      const section = document.createElement('section');
      section.className = 'caos-metadata-search__group';
      section.dataset.kind = g.type;

      const heading = document.createElement('h3');
      heading.className = 'caos-metadata-search__group-head';
      // Says how many there were, not how many are shown: a capped group that read "8" would be
      // telling somebody they had seen everything when they had seen a third of it.
      heading.textContent =
        g.hits.length < g.total ? `${kindLabel(g.type)} — ${g.hits.length} of ${g.total}` : `${kindLabel(g.type)} (${g.total})`;
      section.append(heading);

      const list = document.createElement('ul');
      list.className = 'caos-metadata-search__hits';
      for (const hit of g.hits) {
        list.append(drawHit(hit));
      }
      section.append(list);
      results.append(section);
    }
  }

  /**
   * One hit, as a CARD describing the thing: what it is called, its API name, its kind, and — when
   * something installed can act on it — a link onward to the screen that can.
   */
  function drawHit(hit: MetadataSearchHit): HTMLElement {
    const li = document.createElement('li');
    li.className = 'caos-metadata-search__hit';
    li.dataset.key = hit.key;
    li.dataset.type = hit.type;

    const path = destinationFor(destinations, hit);
    const title = document.createElement(path ? 'a' : 'span');
    title.className = 'caos-metadata-search__hit-label';
    title.textContent = hit.label;
    if (path) {
      const a = title as HTMLAnchorElement;
      a.href = path;
      a.addEventListener('click', (ev) => {
        if (!options.navigate) return;
        ev.preventDefault();
        close();
        options.navigate(path);
      });
    }

    const api = document.createElement('span');
    api.className = 'caos-metadata-search__hit-key';
    api.textContent = hit.key;

    li.append(title, api);

    // Why this row is here, when the label on it does not say so. Without this a hit matched only on
    // its API name reads as a wrong answer.
    if (hit.matchedOn === 'key' && hit.label !== hit.key) {
      const why = document.createElement('span');
      why.className = 'caos-metadata-search__hit-why';
      why.textContent = 'matched its API name';
      li.append(why);
    }
    if (!path) {
      // Said plainly rather than drawn as a dead link: nothing installed browses this kind.
      const none = document.createElement('span');
      none.className = 'caos-metadata-search__hit-nowhere';
      none.textContent = 'no screen installed for this kind';
      li.append(none);
    }
    return li;
  }

  async function run(term: string): Promise<void> {
    lastTerm = term;
    const mine = ++issued;
    if (!term.trim()) {
      kinds.replaceChildren();
      results.replaceChildren();
      status.textContent = '';
      return;
    }
    await loadRosters();
    const query = `/api/metadata/search?q=${encodeURIComponent(term)}${activeKind ? `&type=${encodeURIComponent(activeKind)}` : ''}`;
    type SearchPayload = { groups?: MetadataSearchGroup[]; total?: number } | null;
    let payload: SearchPayload = null;
    try {
      const res = await searchFetch(query);
      payload = (await res.json()) as SearchPayload;
    } catch {
      // A search that could not be asked says so, rather than looking like an org with nothing in it.
      if (mine !== issued) return;
      kinds.replaceChildren();
      results.replaceChildren();
      status.textContent = 'Could not search just now.';
      return;
    }
    // A slow earlier answer never replaces a newer one — the same rule CAOS-135's box already keeps.
    if (mine !== issued) return;

    const groups = payload?.groups ?? [];
    const total = payload?.total ?? 0;
    // The kind buttons are drawn from an UNNARROWED search only. Drawing them from a narrowed one
    // would leave a single button and no way back to the other kinds.
    if (!activeKind) drawKinds(groups);
    drawGroups(groups);
    status.textContent = total === 0 ? `Nothing matches “${term}”.` : `${total} match${total === 1 ? '' : 'es'}.`;
  }

  const onInput = (): void => {
    void run(input.value);
  };
  input.addEventListener('input', onInput);
  closeBtn.addEventListener('click', close);

  const overlay: MetadataSearchOverlay = {
    element: root,
    open(): void {
      if (open || isTurnedOff()) return;
      open = true;
      root.hidden = false;
      root.dataset.edge = currentEdge();
      input.focus();
      input.select();
    },
    close,
    isOpen: () => open,
    search: (term: string) => {
      input.value = term;
      return run(term);
    },
    async moveTo(edge: MetadataSearchEdge): Promise<void> {
      root.dataset.edge = edge;
      await setPreference(EDGE_PREFERENCE, edge);
    },
    destroy(): void {
      close();
      input.removeEventListener('input', onInput);
      root.remove();
    },
  };
  return overlay;
}

/**
 * The keyboard seam: Ctrl/Cmd+K opens it, Escape closes it, and both are reachable without the mouse
 * from any page because this listens at the document.
 *
 * It does NOT intercept the keystroke when the person has turned the overlay off — a shortcut that
 * swallows a key for a surface somebody has switched off is worse than no shortcut.
 *
 * Returns the detach function, so a frame that is rebuilt does not leave a second listener behind
 * answering for an overlay that is no longer on the page.
 */
export function bindMetadataSearchKeys(overlay: MetadataSearchOverlay): () => void {
  const onKey = (ev: KeyboardEvent): void => {
    if ((ev.ctrlKey || ev.metaKey) && !ev.altKey && ev.key.toLowerCase() === 'k') {
      if (isTurnedOff()) return;
      ev.preventDefault();
      if (overlay.isOpen()) overlay.close();
      else overlay.open();
      return;
    }
    // Closes the same way it opens, which is what the ticket asks for, and the same way every other
    // panel in this frame closes.
    if (ev.key === 'Escape' && overlay.isOpen()) {
      ev.preventDefault();
      overlay.close();
    }
  };
  document.addEventListener('keydown', onKey);
  return () => document.removeEventListener('keydown', onKey);
}
